import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, Phone } from 'lucide-react';

const CTASection = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      title: "Book Your Professional Cleaning Today",
      subtitle: "Join 1,500+ satisfied customers in Athens who trust us with their homes and offices",
      bookButton: "Book A Viewing",
      callButton: "Call Now",
      guarantee: "100% Satisfaction Guaranteed"
    },
    gr: {
      title: "Κλείστε τον Επαγγελματικό σας Καθαρισμό Σήμερα",
      subtitle: "Ενταχθείτε στους 1.500+ ικανοποιημένους πελάτες στην Αθήνα που μας εμπιστεύονται τα σπίτια και τα γραφεία τους",
      bookButton: "Κλείστε Ραντεβού",
      callButton: "Καλέστε Τώρα",
      guarantee: "100% Εγγύηση Ικανοποίησης"
    }
  };

  const t = content[language];

  return (
    <section className="py-20 relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-cyan-50 to-sky-50"></div>
      
      {/* Decorative circles */}
      <div className="absolute top-10 left-10 w-72 h-72 bg-gradient-to-br from-blue-200/30 to-cyan-200/30 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-gradient-to-br from-cyan-200/30 to-sky-200/30 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>

      <div className="container relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <div className="animate-fadeInUp">
            <div className="inline-block px-4 py-2 rounded-full mb-6" style={{ background: 'rgba(135, 206, 235, 0.15)' }}>
              <span className="text-sm font-semibold" style={{ color: 'var(--accent-text)' }}>
                {t.guarantee}
              </span>
            </div>
            <h2 className="heading-1 mb-6">{t.title}</h2>
            <p className="body-large mb-10 max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
              {t.subtitle}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            <button
              onClick={() => {
                const element = document.getElementById('booking');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="btn-primary flex items-center gap-2 group"
            >
              {t.bookButton}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <a
              href="tel:+306994411844"
              className="btn-secondary flex items-center gap-2"
            >
              <Phone className="w-5 h-5" />
              {t.callButton}
            </a>
          </div>

          {/* Trust indicators */}
          <div className="mt-12 flex flex-wrap justify-center gap-8 text-sm animate-fadeInUp" style={{ animationDelay: '0.4s', color: 'var(--text-secondary)' }}>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 12l-3-3m0 0l-3 3m3-3v12M13 4h7m0 0v7m0-7l-7 7"></path>
              </svg>
              <span>{language === 'en' ? 'Free Estimates' : 'Δωρεάν Εκτιμήσεις'}</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
              </svg>
              <span>{language === 'en' ? 'Licensed & Professional' : 'Αδειοδοτημένοι & Ασφαλισμένοι'}</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732l-3.354 1.935-1.18 4.455a1 1 0 01-1.933 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732l3.354-1.935 1.18-4.455A1 1 0 0112 2z" clipRule="evenodd"></path>
              </svg>
              <span>{language === 'en' ? 'Professional Equipment' : 'Επαγγελματικός Εξοπλισμός'}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;
